import {
  Discount,
  DiscountKind,
  DiscountRequest,
  DiscountValueType,
  MenuCategory,
  MenuItem,
} from '../../types/api';
import type { TranslationSchema } from '../../locales';
import { buildCategoryTree, flattenCategoryTree, subtreeIds } from '../../utils/categoryTree';
import { PricedItem, comboPieces, comboRegularPrice, priceExtremes } from '../../utils/discountPricing';
import { formatTenge } from '../../utils/money';
import { fill } from '../../utils/template';

/**
 * Черновик формы скидки и всё, что с ним делается без React: разбор ответа
 * бэка, сборка тела запроса, проверки и раскладка ошибок 422 по полям.
 *
 * Проверки повторяют серверные (ADR-026 §2), последнее слово — за сервером:
 * его 422 показывается под теми же полями.
 */

export type DiscountStrings = TranslationSchema['discounts'];

/** Цель групповой скидки — явный режим, а не «что выбрано последним». */
export type DiscountTarget = 'category' | 'items';

export interface ComboLine {
  menuItemId: number;
  quantity: number;
}

/** Числа — сырыми строками: иначе поле нельзя очистить, оно превращается в 0. */
export interface DiscountDraft {
  name: string;
  kind: DiscountKind;
  target: DiscountTarget;
  categoryId: number | null;
  itemIds: number[];
  valueType: DiscountValueType;
  percent: string;
  amount: string;
  comboLines: ComboLine[];
  bundlePrice: string;
  /** «HH:MM» — формат поля time. */
  timeStart: string;
  timeEnd: string;
  weekdays: number[];
  /** Y-M-D, пусто — без ограничения. */
  startsAt: string;
  endsAt: string;
}

/** Под каким полем показать текст ошибки. */
export type DiscountField =
  | 'name'
  | 'target'
  | 'value'
  | 'combo'
  | 'bundle'
  | 'time'
  | 'weekdays'
  | 'starts_at'
  | 'ends_at'
  | 'general';

export type DiscountErrors = Partial<Record<DiscountField, string>>;

export const PERCENT_MIN = 1;
export const PERCENT_MAX = 100;
export const AMOUNT_MIN = 1;
export const COMBO_QUANTITY_MIN = 1;
export const COMBO_QUANTITY_MAX = 10;
/** «Две позиции или одна дважды». */
export const COMBO_PIECES_MIN = 2;

const DEFAULT_PERCENT = '10';
const DEFAULT_TIME_START = '10:00';
const DEFAULT_TIME_END = '12:00';

export function emptyDraft(): DiscountDraft {
  return {
    name: '',
    kind: 'group',
    target: 'category',
    categoryId: null,
    itemIds: [],
    valueType: 'percent',
    percent: DEFAULT_PERCENT,
    amount: '',
    comboLines: [],
    bundlePrice: '',
    timeStart: DEFAULT_TIME_START,
    timeEnd: DEFAULT_TIME_END,
    // Дни партнёр выбирает сам (решение владельца 20.10.2026): заранее не
    // отмечен ни один, а без дня форма не сохраняется (validateDraft).
    weekdays: [],
    // Пустой срок — акция бессрочная: так вели себя все акции до появления дат.
    startsAt: '',
    endsAt: '',
  };
}

function isWeekday(day: unknown): day is number {
  return typeof day === 'number' && Number.isInteger(day) && day >= 0 && day <= 6;
}

/** «HH:MM:SS» → «HH:MM». null из админки — пустое поле, а не падение формы. */
function timeForInput(value: string | null | undefined): string {
  return typeof value === 'string' && /^\d{2}:\d{2}/.test(value) ? value.slice(0, 5) : '';
}

/** null — дни не заданы и скидка не идёт; подставлять понедельник нельзя. */
function weekdaysOf(discount: Discount): number[] {
  if (Array.isArray(discount.weekdays)) return discount.weekdays.filter(isWeekday);

  return isWeekday(discount.weekday) ? [discount.weekday] : [];
}

export function clampQuantity(quantity: number): number {
  const whole = Math.round(quantity);
  if (!Number.isFinite(whole)) return COMBO_QUANTITY_MIN;

  return Math.min(COMBO_QUANTITY_MAX, Math.max(COMBO_QUANTITY_MIN, whole));
}

/** Ответ бэка → черновик. Отсутствующие поля до ADR-026 — `group` + `percent`. */
export function discountToDraft(discount: Discount): DiscountDraft {
  const base = emptyDraft();
  const kind: DiscountKind = discount.kind === 'combo' ? 'combo' : 'group';
  const valueType: DiscountValueType = discount.value_type === 'amount' ? 'amount' : 'percent';
  const categoryId = discount.menu_category_id ?? discount.menu_category?.id ?? null;
  const isCategory =
    discount.discount_type === 'category' || (discount.discount_type == null && categoryId != null);
  const itemIds = discount.menu_item_ids ?? discount.menu_items?.map((item) => item.id) ?? [];
  const isGroup = kind === 'group';

  return {
    ...base,
    name: discount.name ?? '',
    kind,
    target: isGroup && !isCategory ? 'items' : 'category',
    categoryId: isGroup && isCategory ? categoryId : null,
    itemIds: isGroup && !isCategory ? itemIds : [],
    valueType,
    // У тенге и комбо `discount` — лишь эквивалент: в поле процента его не кладём.
    percent:
      isGroup && valueType === 'percent' && Number.isFinite(discount.discount)
        ? String(discount.discount)
        : base.percent,
    amount: discount.amount != null ? String(discount.amount) : '',
    comboLines: (discount.combo_items ?? []).map((line) => ({
      menuItemId: line.menu_item_id,
      quantity: clampQuantity(line.quantity),
    })),
    bundlePrice: discount.bundle_price != null ? String(discount.bundle_price) : '',
    timeStart: timeForInput(discount.time_start),
    timeEnd: timeForInput(discount.time_end),
    weekdays: weekdaysOf(discount),
    startsAt: discount.starts_at ?? '',
    endsAt: discount.ends_at ?? '',
  };
}

/** Целое неотрицательное число из поля ввода; пусто и мусор — null. */
export function parseWhole(raw: string): number | null {
  const value = raw.trim();

  return /^\d+$/.test(value) ? Number(value) : null;
}

/** «HH:MM» поля → «HH:MM:SS» бэка. */
function apiTime(value: string): string {
  return `${value.slice(0, 5)}:00`;
}

/**
 * Черновик → тело запроса по контракту ADR-026. Вызывается после проверок,
 * поэтому запасные нули ниже — для типов, а не для отправки.
 */
export function draftToRequest(draft: DiscountDraft): DiscountRequest {
  const schedule = {
    // Пустое поле — снятое значение, а не «не трогать»: бэк отличает null от
    // отсутствующего ключа, и без null имя и срок нельзя было бы убрать.
    name: draft.name.trim() || null,
    time_start: apiTime(draft.timeStart),
    time_end: apiTime(draft.timeEnd),
    weekdays: [...draft.weekdays].sort((a, b) => a - b),
    starts_at: draft.startsAt || null,
    ends_at: draft.endsAt || null,
  };

  if (draft.kind === 'combo') {
    return {
      ...schedule,
      kind: 'combo',
      bundle_price: parseWhole(draft.bundlePrice) ?? 0,
      combo_items: draft.comboLines.map((line) => ({
        menu_item_id: line.menuItemId,
        quantity: line.quantity,
      })),
    };
  }

  const value =
    draft.valueType === 'amount'
      ? { value_type: 'amount' as const, amount: parseWhole(draft.amount) ?? 0 }
      : { value_type: 'percent' as const, discount: parseWhole(draft.percent) ?? 0 };

  // Цель — ровно одна. Раньше раздел со снятыми галочками уходил разделом, и
  // скидка молча ложилась на весь раздел.
  const target =
    draft.target === 'category'
      ? { menu_category_id: draft.categoryId ?? 0 }
      : { menu_item_ids: [...draft.itemIds] };

  return { ...schedule, kind: 'group', ...value, ...target };
}

/** Позиции раздела вместе с подразделами: у ветки своих позиций нет. */
export function itemsInCategory(
  categories: MenuCategory[],
  items: MenuItem[],
  categoryId: number,
): MenuItem[] {
  const node = flattenCategoryTree(buildCategoryTree(categories)).find((n) => n.id === categoryId);
  if (!node) return [];

  const ids = new Set(subtreeIds(node));

  return items.filter((item) => ids.has(item.menu_category_id));
}

/** Состав комбо после галочек: старые строки хранят количество, новые — по штуке. */
export function syncComboLines(lines: ComboLine[], itemIds: number[]): ComboLine[] {
  const kept = lines.filter((line) => itemIds.includes(line.menuItemId));
  const added = itemIds
    .filter((id) => !lines.some((line) => line.menuItemId === id))
    .map((id) => ({ menuItemId: id, quantity: COMBO_QUANTITY_MIN }));

  return [...kept, ...added];
}

/**
 * Что форма знает о ценах. null — цены неизвестны (меню не загрузилось):
 * сверку с ценой тогда делает только сервер.
 */
export interface PricingContext {
  targetItems: PricedItem[] | null;
  comboLines: Array<PricedItem & { quantity: number }> | null;
}

/**
 * Проверки, завязанные на цены меню. Показываются сразу, по ходу ввода: что
 * скидка в тенге больше цены позиции, партнёр должен увидеть до «Сохранить».
 */
export function priceErrors(
  draft: DiscountDraft,
  context: PricingContext,
  strings: DiscountStrings,
): DiscountErrors {
  if (draft.kind === 'group') {
    const amount = parseWhole(draft.amount);
    if (draft.valueType !== 'amount' || amount == null || amount < AMOUNT_MIN || !context.targetItems) {
      return {};
    }

    const extremes = priceExtremes(context.targetItems);
    if (!extremes) {
      // Пустой список отмеченных позиций ловит проверка цели; здесь — раздел без позиций.
      return draft.target === 'category' && draft.categoryId != null
        ? { value: strings.errorAmountNoItems }
        : {};
    }

    return amount > extremes.cheapest.price
      ? {
          value: fill(strings.errorAmountTooBig, {
            amount: formatTenge(amount),
            name: extremes.cheapest.name,
            price: formatTenge(extremes.cheapest.price),
          }),
        }
      : {};
  }

  const bundle = parseWhole(draft.bundlePrice);
  if (bundle == null || !context.comboLines || context.comboLines.length === 0) return {};

  const regular = comboRegularPrice(context.comboLines);

  return bundle >= regular
    ? { bundle: fill(strings.errorBundleTooHigh, { price: formatTenge(regular) }) }
    : {};
}

/** Все проверки перед отправкой. Пустой объект — можно сохранять. */
export function validateDraft(
  draft: DiscountDraft,
  context: PricingContext,
  strings: DiscountStrings,
): DiscountErrors {
  const errors: DiscountErrors = {};

  if (draft.kind === 'group') {
    if (draft.target === 'category' && draft.categoryId == null) {
      errors.target = strings.errorTargetCategory;
    }
    if (draft.target === 'items' && draft.itemIds.length === 0) {
      errors.target = strings.errorTargetItems;
    }

    if (draft.valueType === 'percent') {
      const percent = parseWhole(draft.percent);
      if (percent == null || percent < PERCENT_MIN || percent > PERCENT_MAX) {
        errors.value = strings.errorPercent;
      }
    } else {
      const amount = parseWhole(draft.amount);
      if (amount == null || amount < AMOUNT_MIN) errors.value = strings.errorAmount;
    }
  } else {
    if (comboPieces(draft.comboLines) < COMBO_PIECES_MIN) errors.combo = strings.errorCombo;
    if (parseWhole(draft.bundlePrice) == null) errors.bundle = strings.errorBundle;
  }

  if (!draft.timeStart || !draft.timeEnd) {
    errors.time = strings.errorTime;
  } else if (draft.timeStart === draft.timeEnd) {
    // Окно через полночь (23:00 → 02:00) — рабочий случай для баров, а не
    // ошибка ввода. Запрещено только окно нулевой длины.
    errors.time = strings.errorTimeSame;
  }

  if (draft.weekdays.length === 0) errors.weekdays = strings.errorDays;

  // Одна дата законна: «уже идёт, до 30 июня» и «с 1 июля, бессрочно».
  if (draft.startsAt && draft.endsAt && draft.endsAt < draft.startsAt) {
    errors.ends_at = strings.errorEndsAt;
  }

  // Ошибка формата важнее ценовой: «укажите сумму» прежде «сумма больше цены».
  return { ...priceErrors(draft, context, strings), ...errors };
}

/** Ключи Laravel → поля формы. `discount_type` — старое имя ошибки цели. */
const SERVER_FIELDS: Array<[RegExp, DiscountField]> = [
  [/^name$/, 'name'],
  [/^(discount|amount|value_type)$/, 'value'],
  [/^(menu_category_id|menu_item_ids(\..+)?|discount_type)$/, 'target'],
  [/^combo_items(\..+)?$/, 'combo'],
  [/^bundle_price$/, 'bundle'],
  [/^(time_start|time_end)$/, 'time'],
  [/^weekdays(\..+)?$/, 'weekdays'],
  [/^starts_at$/, 'starts_at'],
  [/^ends_at$/, 'ends_at'],
];

/**
 * Ошибки 422 в полях формы. На поле — первый текст: второй почти всегда
 * следствие первого (`combo_items.1.quantity` после `combo_items`).
 */
export function mapServerErrors(fieldErrors: Record<string, string>): DiscountErrors {
  return Object.entries(fieldErrors).reduce<DiscountErrors>((acc, [key, message]) => {
    const field = SERVER_FIELDS.find(([pattern]) => pattern.test(key))?.[1] ?? 'general';

    return acc[field] ? acc : { ...acc, [field]: message };
  }, {});
}

/** Поля, которых у этого вида скидки в форме нет. */
const FIELDS_ABSENT_IN: Record<DiscountKind, DiscountField[]> = {
  group: ['combo', 'bundle'],
  combo: ['target', 'value'],
};

/**
 * Ошибка поля, которого в этом виде формы нет, уходит в общую строку —
 * иначе 422 про цель у комбо осталась бы невидимой.
 */
export function placeErrors(errors: DiscountErrors, kind: DiscountKind): DiscountErrors {
  const hidden = FIELDS_ABSENT_IN[kind];

  return Object.entries(errors).reduce<DiscountErrors>((acc, [key, message]) => {
    const field = hidden.includes(key as DiscountField) ? 'general' : (key as DiscountField);

    return acc[field] ? acc : { ...acc, [field]: message };
  }, {});
}
